/**
 * Update WRO Dialog
 *
 * Edits a linked ShipBob WRO IN PLACE: its quantities (to the PO's), expected
 * arrival date, the PO # it shows in ShipBob, and the container / tracking
 * number (usually only known weeks after the WRO is created). The WRO keeps
 * its number and its box labels — the factory has already applied those, so
 * this never cancels and recreates a WRO. Only what changed is sent.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import InventoryService from "services/InventoryService";
import { WRO } from "models/InventoryModel";

// ShipBob only takes quantity and arrival-date changes while the WRO is awaiting arrival
const WRO_STATUS_AWAITING = "Awaiting";
// ShipBob rejects any other character in a WRO's PO number
const WRO_PO_NUMBER_PATTERN = /^[A-Za-z0-9 _-]*$/;

interface UpdateWroDialogProps {
  open: boolean;
  onClose: () => void;
  wro: WRO;
  poNumber: string;
  poItems: Array<{ inventoryId: string; inventoryName?: string; quantity: number }>;
  /** Set when the WRO also carries units for another PO: its contents must be changed in ShipBob */
  quantitiesLockedReason?: string | null;
  onUpdated: (wro: { wroId: string; fulfillmentCenterName: string | null }) => void;
}

export default function UpdateWroDialog({
  open,
  onClose,
  wro,
  poNumber,
  poItems,
  quantitiesLockedReason = null,
  onUpdated,
}: UpdateWroDialogProps): JSX.Element {
  const service = useMemo(() => new InventoryService(), []);
  const currentWroPoNumber = wro.purchaseOrderNumber || "";
  const currentArrivalDate = wro.expectedArrivalDate || "";
  const [wroPoNumber, setWroPoNumber] = useState(currentWroPoNumber);
  const [expectedArrivalDate, setExpectedArrivalDate] = useState(currentArrivalDate);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [updateQuantities, setUpdateQuantities] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Union of PO items and WRO lines: what the WRO would hold (the PO's
  // quantities) next to what it holds today.
  const wroQtyById = new Map(wro.lines.map(line => [String(line.inventoryId), line.expectedQuantity]));
  const rows = poItems
    .filter(item => item.quantity >= 1)
    .map(item => ({
      inventoryId: item.inventoryId,
      name: item.inventoryName || `Inventory ${item.inventoryId}`,
      wroQty: wroQtyById.get(String(item.inventoryId)) ?? null,
      poQty: item.quantity,
    }));
  const droppedLines = wro.lines.filter(line =>
    !poItems.some(item => String(item.inventoryId) === String(line.inventoryId) && item.quantity >= 1));
  const quantitiesDiffer = droppedLines.length > 0 || rows.some(row => row.wroQty !== row.poQty);
  const isAwaiting = wro.status === WRO_STATUS_AWAITING;
  const quantitiesLocked = Boolean(quantitiesLockedReason) || !isAwaiting;

  useEffect(() => {
    if (!open) return;
    setWroPoNumber(currentWroPoNumber);
    setExpectedArrivalDate(currentArrivalDate);
    setTrackingNumber("");
    setUpdateQuantities(quantitiesDiffer && !quantitiesLocked);
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, wro.wroId]);

  const trimmedWroPoNumber = wroPoNumber.trim();
  const upperWroPo = trimmedWroPoNumber.toUpperCase();
  const upperPo = poNumber.trim().toUpperCase();
  // Mirrors the server (updateReceivingOrder): a rename must not cut the WRO loose. When the
  // WRO is this PO's by name, the PO number stays in front; when it is linked another way (a
  // hand allocation, or a consolidated "245AB US …" container) its first word stays.
  const matchesByName = (value: string) => value.startsWith(upperPo) && !/[A-Z0-9]/.test(value.charAt(upperPo.length));
  const firstWord = (value: string) => value.trim().toUpperCase().split(/\s+/)[0];
  const linkedByName = matchesByName(currentWroPoNumber.trim().toUpperCase());
  const linksBack = linkedByName ? matchesByName(upperWroPo) : firstWord(wroPoNumber) === firstWord(currentWroPoNumber);
  const wroPoNumberError = !trimmedWroPoNumber ? "The WRO needs a PO #"
    : !WRO_PO_NUMBER_PATTERN.test(trimmedWroPoNumber) ? "ShipBob only accepts letters, numbers, spaces, hyphens and underscores"
    : !linksBack ? (linkedByName
      ? `Must start with "${poNumber}" followed by a space — otherwise the WRO will not link back to this PO`
      : `Must keep starting with "${firstWord(currentWroPoNumber)}" — that is what links this WRO to its POs`)
    : null;

  const changes = {
    wroPoNumber: trimmedWroPoNumber !== currentWroPoNumber.trim(),
    expectedArrivalDate: /^\d{4}-\d{2}-\d{2}$/.test(expectedArrivalDate) && expectedArrivalDate !== currentArrivalDate,
    trackingNumber: trackingNumber.trim().length > 0,
    quantities: updateQuantities && quantitiesDiffer && !quantitiesLocked && rows.length > 0,
  };
  const changeCount = Object.values(changes).filter(Boolean).length;
  const isFormValid = changeCount > 0 && !wroPoNumberError;

  const handleUpdate = async () => {
    if (!isFormValid) return;
    setIsUpdating(true);
    setErrorMessage(null);
    try {
      const { wro: updated } = await service.updateWro(wro.wroId, {
        poNumber,
        ...(changes.wroPoNumber && { wroPoNumber: trimmedWroPoNumber }),
        ...(changes.expectedArrivalDate && { expectedArrivalDate }),
        ...(changes.trackingNumber && { trackingNumber: trackingNumber.trim() }),
        ...(changes.quantities && { items: rows.map(row => ({ inventoryId: Number(row.inventoryId), quantity: row.poQty })) }),
      });
      onUpdated(updated);
      onClose();
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={open} onClose={isUpdating ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update WRO {wro.wroId}</DialogTitle>
      <DialogContent>
        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>{errorMessage}</Alert>
        )}
        <Alert severity="info" sx={{ mb: 2 }}>
          This edits WRO {wro.wroId} in ShipBob. <strong>It keeps the same WRO number and the same box labels</strong> —
          nothing new needs to go to the manufacturer.
        </Alert>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
          <TextField
            size="small"
            label="PO # on the WRO"
            value={wroPoNumber}
            onChange={(e) => setWroPoNumber(e.target.value)}
            error={Boolean(wroPoNumberError)}
            helperText={wroPoNumberError ?? `Add a note after the PO number if it helps, e.g. "${poNumber} DHAP Wedge"`}
            sx={{ width: "100%" }}
          />
          <DatePicker
            label="Expected arrival"
            value={expectedArrivalDate ? moment(expectedArrivalDate) : null}
            disabled={!isAwaiting}
            onChange={(newValue: any) => {
              if (newValue && newValue.isValid()) setExpectedArrivalDate(newValue.format("YYYY-MM-DD"));
            }}
            slotProps={{ textField: { size: "small", sx: { width: 190 }, helperText: isAwaiting ? undefined : `Locked — WRO is ${wro.status}` } }}
          />
          <TextField
            size="small"
            label="Container # / tracking #"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            helperText="Enter it once you have it. Leave blank to keep what ShipBob has."
            sx={{ flex: 1, minWidth: 240 }}
          />
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Quantities</Typography>
        {quantitiesLockedReason ? (
          <Alert severity="warning" sx={{ mb: 1 }}>{quantitiesLockedReason}</Alert>
        ) : !isAwaiting ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            WRO {wro.wroId} is {wro.status} — ShipBob only allows quantity changes while a WRO is awaiting arrival.
            Differences get reconciled at receiving.
          </Alert>
        ) : !quantitiesDiffer ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>The WRO already matches this PO's quantities.</Typography>
        ) : (
          <FormControlLabel
            control={<Checkbox size="small" checked={updateQuantities} onChange={(e) => setUpdateQuantities(e.target.checked)} />}
            label={`Set the WRO's quantities to ${poNumber}'s`}
          />
        )}
        {quantitiesDiffer && (
          <Table size="small" sx={{ mb: 1, opacity: changes.quantities ? 1 : 0.55 }}>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="right">WRO today</TableCell>
                <TableCell align="right">{poNumber}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.inventoryId}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell align="right">{row.wroQty ?? "—"}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: row.wroQty !== row.poQty ? 700 : 400 }}>{row.poQty}</TableCell>
                </TableRow>
              ))}
              {droppedLines.map(line => (
                <TableRow key={`dropped-${line.inventoryId}`}>
                  <TableCell>{line.sku || `Inventory ${line.inventoryId}`}</TableCell>
                  <TableCell align="right">{line.expectedQuantity}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>removed</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUpdating}>Cancel</Button>
        <Button variant="contained" onClick={handleUpdate} disabled={!isFormValid || isUpdating}>
          {isUpdating ? "Updating in ShipBob…" : changeCount > 0 ? `Update WRO (${changeCount} change${changeCount === 1 ? "" : "s"})` : "Nothing to update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
